/**
 * The original's own stars, drawn — 31 hand-placed points out of `STARS.DAT`,
 * which `time-of-day-design.md` §5 recovered and left undrawn pending "a bake
 * stage so the data stays reproducible rather than transcribed into source".
 * That stage is `tools/bake/src/stages/stars.ts`; this reads its output.
 *
 * ## Which treatment draws this
 *
 * `original`, and only that one — the treatment a pilot flies with ENHANCED SKY
 * off. This is the *authentic* night sky and it hangs with the rest of the
 * authentic picture: `sun.c`'s banded dome and its half-a-turn-opposite-the-sun
 * moon. It used to hang under `enhanced` instead, for the uninteresting reason
 * that `enhanced` was the only state that drew anything at all at night;
 * `enhanced` now draws the sky that was really overhead (`night-sky.ts`, 518
 * catalogue stars placed by date and latitude), and two constellations in one
 * sky is one too many.
 *
 * That move also puts the compass caveat below where it belongs. It is a
 * statement about *this* field, which is plotted from `sun.c`'s own recovered
 * angles through a handedness that was never recovered; the catalogue field
 * has no such freedom and `night-sky-look.test.ts` pins it against the Plough.
 *
 * ## What is the original's, and what is this port's
 *
 * **The original's:** the count, the four magnitudes (`BARON.PAL` 195-198), and
 * every azimuth and angle. `sun.c` un-hides them on its own `sin(sunAngle) < 0`
 * test, so they appear exactly when the moon does. They are plotted as **single
 * pixels** — the sky pass's non-bitmap branch — with no size, no twinkle and no
 * blending, and that is reproduced: a star here is one original pixel of a
 * 200-line frame, sized through `sprite-screen-size.ts` like every other sprite
 * in the port.
 *
 * **This port's:** the compass handedness. `sun.c`'s own `sin`/`cos` pairing was
 * not recovered, so `celestialDirection`'s convention is used — the same one the
 * sun already rides, which keeps one convention in the file but *may mirror the
 * constellations east-for-west*. That is the one honest caveat on this layer and
 * it is why the groups are not named after any real constellation.
 *
 * **Also this port's:** the horizon fade. A star near the skyline is faded
 * toward the dome's own horizon colour rather than blended out, because an
 * opaque quad is what avoids `screen-sprites.ts`'s measured `transparent` +
 * `depthWrite: false` hazard, and a star the colour of the sky behind it is
 * invisible without any blending at all. Every fade step is therefore still a
 * mix of two palette entries, which `ordered-dither.ts` would put back on the
 * DAC grid anyway.
 *
 * ## Not reproduced
 *
 * The sky precesses in the original — `sun.c` `0xf99f` advances every object by
 * one unit every 300 ticks (the `STARS.DAT` header), and stars carry a
 * `(day + month * 30) * 0xB6` seasonal term on top. This port has four hour
 * buckets and no clock, so the field is static. Stars whose recovered angle puts
 * them below the horizon are dropped at build time: the ground would paint over
 * them anyway, and 20 of the 31 are down at this port's one night hour.
 */

import * as THREE from "three/webgpu";
import { CAMERA_FOV_DEG } from "./flight-view.ts";
import { celestialDirection, type PaletteColour } from "./time-of-day.ts";
import { mixPalette } from "./enhanced-sky-look.ts";
import { metresPerScreenPixel, metresPerScreenPixelX } from "../three/sprite-screen-size.ts";
import { CELESTIAL_DISTANCE_M } from "./world.ts";

/** `tools/bake/src/stages/stars.ts`' output, as the runtime reads it. */
export interface BakedStarTable {
  readonly stars: readonly { readonly colour: number; readonly azimuth: number; readonly angle: number }[];
}

export const BAKED_STARS_URL = "/baked/sky/stars.json";

/** A turn in `sun.c`'s 16-bit angle units, restated here so the conversion is not a magic 65536. */
const TURN = 0x10000;

/** One original pixel of a 200-line frame — the sky pass's own plot. */
const STAR_PIXELS = 1;

/**
 * Below this elevation a star has faded fully into the horizon colour. Twelve
 * degrees, which is the same band `time-of-day.ts`'s twilight concentration
 * spreads its wash over — one number for "how thick the haze is", not two.
 */
export const STAR_FADE_ELEVATION_DEG = 12;

/** After the dome (`-1`), before the sun disc (`-0.5`) — stars are behind the moon, not in front of it. */
const STAR_RENDER_ORDER = -0.75;

/** One star resolved into world axes, with its fade already folded into its colour. */
export interface PlacedStar {
  readonly direction: THREE.Vector3;
  readonly colour: PaletteColour;
}

/**
 * The visible stars, faded toward `horizon`. Pure, so
 * `star-field.test.ts` can check the drop and the fade without a GPU.
 */
export function placeStars(table: BakedStarTable, horizon: PaletteColour): PlacedStar[] {
  const fadeSine = Math.sin((STAR_FADE_ELEVATION_DEG * Math.PI) / 180);
  const placed: PlacedStar[] = [];
  for (const star of table.stars) {
    const direction = celestialDirection(star.angle / TURN, star.azimuth);
    if (direction.y <= 0) continue;
    const fade = Math.min(direction.y / fadeSine, 1);
    placed.push({
      direction: new THREE.Vector3(direction.x, direction.y, direction.z),
      colour: mixPalette(horizon, star.colour, fade),
    });
  }
  return placed;
}

/**
 * The star quads, built **once** and thereafter only moved.
 *
 * The first version of this rewrote all 44 vertices every frame from the
 * camera's basis, the way `screen-sprites.ts` bills its sprites. It drew
 * *nothing*: under this renderer a `needsUpdate` set inside `onBeforeRender`
 * lands after the attribute for that pass has been taken, so the buffer kept
 * frame zero's contents — 44 vertices at the origin, i.e. eleven zero-area
 * triangles. Nothing logged, nothing threw, the sky was simply empty. (The same
 * shape of failure as the reversed-Z `depthWrite` trap in `lessons.md`: a
 * legitimate-looking flag that silently rasterises no pixels.)
 *
 * The fix is to stop needing the update. A star is 18 km away and the camera is
 * always near the dome's centre, so **the direction from the camera to a star is
 * the star's own bearing** — which means a quad built perpendicular to its own
 * bearing already faces the lens, from anywhere the aeroplane can be. The
 * geometry is therefore static and the mesh just rides the camera, exactly the
 * mechanism `world.ts`'s dome and `sky-dressing.ts`'s disc already use and the
 * one that is known to work here.
 *
 * What that costs is a sub-pixel approximation of the sizing rule: the blit
 * rule wants *view-space* depth, and this uses the range, so a star at the very
 * edge of a wide window is smaller than the rule says by the cosine of its
 * off-axis angle. On a one-pixel star that is a fraction of a pixel, and the
 * alternative is a star field that is not drawn at all.
 */
function starMesh(stars: readonly PlacedStar[]): THREE.Mesh {
  const positions = new Float32Array(stars.length * 4 * 3);
  const colours = new Float32Array(stars.length * 4 * 3);
  const indices: number[] = [];
  const fovY = (CAMERA_FOV_DEG * Math.PI) / 180;
  const halfWidth = (STAR_PIXELS * metresPerScreenPixelX(CELESTIAL_DISTANCE_M, fovY)) / 2;
  const halfHeight = (STAR_PIXELS * metresPerScreenPixel(CELESTIAL_DISTANCE_M, fovY)) / 2;
  const right = new THREE.Vector3();
  const up = new THREE.Vector3();

  stars.forEach((star, index) => {
    const base = index * 4;
    indices.push(base, base + 1, base + 2, base, base + 2, base + 3);
    // Any two axes perpendicular to the bearing will do; world up is only a
    // seed for the cross product and a star straight overhead gets `+X` instead.
    right.set(0, 1, 0).cross(star.direction);
    if (right.lengthSq() < 1e-8) right.set(1, 0, 0);
    right.normalize();
    up.copy(star.direction).cross(right).normalize();

    const corners: readonly (readonly [number, number])[] = [
      [-halfWidth, -halfHeight],
      [halfWidth, -halfHeight],
      [halfWidth, halfHeight],
      [-halfWidth, halfHeight],
    ];
    corners.forEach(([x, y], corner) => {
      const offset = (base + corner) * 3;
      positions[offset] = star.direction.x * CELESTIAL_DISTANCE_M + right.x * x + up.x * y;
      positions[offset + 1] = star.direction.y * CELESTIAL_DISTANCE_M + right.y * x + up.y * y;
      positions[offset + 2] = star.direction.z * CELESTIAL_DISTANCE_M + right.z * x + up.z * y;
      colours[offset] = ((star.colour >> 16) & 0xff) / 255;
      colours[offset + 1] = ((star.colour >> 8) & 0xff) / 255;
      colours[offset + 2] = (star.colour & 0xff) / 255;
    });
  });

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute("color", new THREE.BufferAttribute(colours, 3));
  geometry.setIndex(indices);

  const mesh = new THREE.Mesh(
    geometry,
    // `DoubleSide` because a quad's winding about its own bearing is whichever
    // way the cross products fell, and a star is a dot either way.
    //
    // **Ordinary opaque depth, unlike the sun disc.** The disc opts out of the
    // depth buffer because it is a *sprite* pasted over the view; a star is a
    // point at a finite distance with cloud and terrain legitimately in front of
    // it, so it wants the plain configuration — which is also the one this
    // renderer is least surprising under. `lessons.md`'s 2026-08-19 rule:
    // `depthWrite: false` changes what a pass means and under a backend you are
    // not the author of it can change whether the object draws at all.
    new THREE.MeshBasicMaterial({ vertexColors: true, side: THREE.DoubleSide, fog: false }),
  );
  mesh.renderOrder = STAR_RENDER_ORDER;
  // Its bounding sphere is recentred as the renderer draws it, which is after
  // the frustum test would have read the stale one — the hazard `createSky`
  // documents.
  mesh.frustumCulled = false;
  mesh.onBeforeRender = (_renderer, _scene, camera) => {
    camera.getWorldPosition(mesh.position);
    mesh.updateMatrixWorld();
  };
  return mesh;
}

/**
 * A group that fills itself in once the baked table has loaded, and stays empty
 * if it never does — a missing bake is a sky without stars, never a thrown
 * error in a render loop. `onLoaded` lets a caller (and the tests) wait for it.
 */
export function createStarField(horizon: PaletteColour): { group: THREE.Group; onLoaded: Promise<number> } {
  const group = new THREE.Group();
  const onLoaded = fetch(BAKED_STARS_URL)
    .then((response) => (response.ok ? (response.json() as Promise<BakedStarTable>) : null))
    .then((table) => {
      if (!table) return 0;
      const stars = placeStars(table, horizon);
      if (stars.length > 0) group.add(starMesh(stars));
      return stars.length;
    })
    .catch(() => 0);
  return { group, onLoaded };
}
